import { ClassificationEventType } from './auditEventTypes';

// Mapping from supported AuditEventTypes to the policy manager key used for that event
export const eventTypeToAuditKeyMap: Record<ClassificationEventType, string> = {
    ApplyDocumentMarkings: 'ApplyDocumentMarkings',
    ApplyHtmlHeadersFooters: 'ApplyHtmlHeadersFooters',
    ApplyAdvancedHeadersFooters: 'ApplyAdvanced_HeadersFooters',
    ApplyPortionMarking: 'ApplyPortionMarking',
    ApplyBodyTag: 'BodyTagging',
    ApplySubjectLabel: 'SubjectLabelling',
    ApplyCustomAction: 'CustomActionApplied',
    ApplyCustomXHeader: 'CustomXHeader',
    ApplyCustomProperty: 'SetCustomProperties',

    // severity suffix handled separately
    Alert: 'Alert',
    RecipientAlert: 'RecipientAlert',
    SetClassification: 'SetClassification',

    EmailSent: 'EmailItemSent',
    EmailSendAnyway: 'SendAnyway',
    EmailSendPrevented: 'SendPrevented',

    ChangeAllowed: 'ChangeAllowed',
    ChangePrevented: 'ChangePrevented',
    Unknown: '__UNKNOWN__',
};

// Mapping from action severity to the suffix used for policy manager audit events
export const actionSeveritytoAuditEventSuffixMapping: Record<string, string> = {
    'Log Only': 'LogOnly',
    Prevent: 'Prevent',
    Warn: 'Warning',
    Information: 'Warning', //information events arent currently included in the audit events so treat it as warning for now
};

// Maps policy manager keys to audit object property paths.
//
// A full audit object is constructed first. Before emission, the object is
// filtered using this mapping so that only properties associated with a
// supported policy key are included in the emitted audit event.
//
// Therefore, only keys defined here are included in emitted audit events.
export const filterKeyToAuditPropertyMapping: Readonly<Record<string, readonly string[]>> = {
    // Root paths
    id: ['id'],
    TenantId: ['TenantId'],
    Date: ['UtcDateTime'],

    // Environment paths
    HostProduct: ['Environment.Host', 'Environment.Platform'],
    HostProductVersion: ['Environment.Version'],

    // Context paths
    PolicyEvent: ['MessageEvent.MessageContext.Event', 'DocumentEvent.DocumentContext.Event'],
    EventType: ['MessageEvent.MessageContext.EventType', 'DocumentEvent.DocumentContext.EventType'],
    User: ['MessageEvent.MessageContext.UserName', 'DocumentEvent.DocumentContext.UserName'],
    ProductName: ['MessageEvent.MessageContext.Product', 'DocumentEvent.DocumentContext.Product'],
    ProductVersion: ['MessageEvent.MessageContext.Version', 'DocumentEvent.DocumentContext.Version'],
    ProductEdition: ['MessageEvent.MessageContext.Platform', 'DocumentEvent.DocumentContext.Platform'],
    Severity: ['MessageEvent.MessageContext.Severity', 'DocumentEvent.DocumentContext.Severity'],

    // Message paths
    Subject: ['MessageEvent.Message.Subject'],
    // 'Attachment.Type': ['MessageEvent.Message.Attachments.Type'],
    'Attachment.Name': ['MessageEvent.Message.Attachments.Name'],
    'Attachment.Size': ['MessageEvent.Message.Attachments.Size'],
    'Attachment.Metadata': ['MessageEvent.Message.Attachments.Metadata'],
    'Recipient.RecipientType': ['MessageEvent.Message.Recipients.RecipientType'],
    'Recipient.isDL': ['MessageEvent.Message.Recipients.IsDistributionList'],
    'Recipient.Address': ['MessageEvent.Message.Recipients.Address'],
    'Recipient.Name': ['MessageEvent.Message.Recipients.DisplayName'],
    Sender: ['MessageEvent.Message.Sender.Address', 'MessageEvent.Message.Sender.DisplayName'],

    // Document paths
    FileName: ['DocumentEvent.Document.Filename'],
    Path: ['DocumentEvent.Document.Path'],

    // Combined paths:
    ItemType: ['MessageEvent.Message.Type', 'DocumentEvent.Document.DocumentType'],
    Metadata: [
        'MessageEvent.Message.Metadata',
        'MessageEvent.Message.OriginalMetadata',
        'DocumentEvent.Document.Metadata',
        'DocumentEvent.Document.OriginalMetadata',
    ],
    // OldValue: ['MessageEvent.Message.OriginalField', 'DocumentEvent.Document.OriginalField'],
    // NewValue: ['MessageEvent.Message.NewField', 'DocumentEvent.Document.NewField'],
} as const;

// NOTE:
//
// The policy schema defines additional audit keys that are not currently
// supported by the audit pipeline (no mapping defined here).
// Computer
// EventId
// ActionDescription
// ActionName
// Attachment.DisplayName
// ComputerIPv4Address
// ComputerIPV6Address
// Condition
// Configuration
// Error
// FieldName
// Justification
// ItemID
// OldVersion
// OldValue
// NewVersion
// NewValue
// Printer
// PreviousModifiedDate
// PolicyName
// RuleName
// Source
// TransactionId
//
// To support any of these keys:
// 1. Add a corresponding mapping entry above.
// 2. Ensure the relevant audit data provider populates the associated property.
